import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Hook } from '@rossum/api-client/hooks';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
} from '@rossum/ui/x-data-grid-pro';
import { ActivityDetailToggle } from '../components/columns/ActivityDetailToggle';
import { CancelActivityColumn } from '../components/columns/CancelActivityColumn';
import { DateTimeColumn } from '../components/columns/DateTimeColumn';
import { DurationColumn } from '../components/columns/DurationColumn';
import { SourceColumn } from '../components/columns/SourceColumn';
import { StatusColumn } from '../components/columns/StatusColumn';
import { TextColumn } from '../components/columns/TextColumn';
import { TransformedActivity } from '../helpers';
import { ColumnWidthsObject } from './useColumnWidths';

type KeyOfColumnWidths = keyof ColumnWidthsObject;

type UseColumnsProps = {
  extensions: Hook[] | undefined;
  isLoadingExtensions: boolean;
  columnWidths?: ColumnWidthsObject;
};

export const useColumns = ({
  extensions,
  isLoadingExtensions,
  columnWidths,
}: UseColumnsProps) => {
  const intl = useIntl();

  const columns: GridColDef<TransformedActivity>[] = useMemo(
    () => [
      {
        field: 'status' satisfies KeyOfColumnWidths,
        sortable: false,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.status',
        }),
        width: 130,
        minWidth: 100,
        renderCell: ({ row }) => <StatusColumn {...row} />,
      },
      {
        field: 'source' satisfies KeyOfColumnWidths,
        sortable: false,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.source',
        }),
        width: 250,
        renderCell: ({ row }) => {
          const extension = extensions?.find(e => e.id === row.hookId);

          return (
            <SourceColumn
              hookId={row.hookId}
              name={extension?.name}
              isLoading={isLoadingExtensions}
            />
          );
        },
      },
      {
        field: 'duration' satisfies KeyOfColumnWidths,
        sortable: false,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.duration',
        }),
        headerAlign: 'center',
        width: 150,
        minWidth: 150,
        align: 'center',
        renderCell: ({ row }) => <DurationColumn {...row} />,
      },
      {
        field: 'start' satisfies KeyOfColumnWidths,
        sortable: false,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.start',
        }),
        width: 220,
        renderCell: ({ row }) => (
          <DateTimeColumn value={row.start ?? undefined} />
        ),
      },
      {
        field: 'end' satisfies KeyOfColumnWidths,
        sortable: false,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.end',
        }),
        width: 220,
        renderCell: ({ row }) => (
          <DateTimeColumn value={row.end ?? undefined} />
        ),
      },
      {
        field: 'annotation' satisfies KeyOfColumnWidths,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.annotation',
        }),
        sortable: false,
        width: 100,
        renderCell: ({ row }) => (
          <TextColumn
            value={'annotationId' in row ? `${row.annotationId ?? '-'}` : '-'}
          />
        ),
      },
      {
        field: 'message' satisfies KeyOfColumnWidths,
        headerName: intl.formatMessage({
          id: 'features.activities.columns.header.message',
        }),
        sortable: false,
        width: 300,
        renderCell: ({ row }) => <TextColumn value={row.message} />,
      },
      {
        field: 'cancelActivity',
        renderHeader: () => null,
        renderCell: ({ row }) => <CancelActivityColumn {...row} />,
        sortable: false,
        width: 56,
      },
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: params => (
          <ActivityDetailToggle isExpanded={params.value} />
        ),
        resizable: false,
      },
    ],
    [extensions, intl, isLoadingExtensions]
  );

  const adjustedWidths = useMemo(() => {
    if (!columnWidths) {
      return columns;
    }

    return columns.map(col => {
      const widths: Record<string, number> = columnWidths;

      return {
        ...col,
        width: widths[col.field] ?? col.width,
      };
    });
  }, [columnWidths, columns]);

  return { columns: adjustedWidths };
};
